import { memo } from "react";
import { ChevronLeft, ChevronRight, PackageOpen } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency, formatDateTime } from "@/shared/lib/formatters";
import { RENTAL_TYPE_LABELS } from "../RentalTypeBadge";
import type { RentalResponseDTO } from "../../types/rental.types";

interface PickupRentalsTableProps {
  rentals: RentalResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onSelect: (rental: RentalResponseDTO) => void;
  onPageChange: (page: number) => void;
}

/**
 * Tabela de aluguéis prontos para retirada. Linha inteira clicável —
 * abre o `RentalPickupDialog` para o aluguel correspondente. Não exibe
 * status (sempre `COMPLETE` por filtro) e omite a coluna de retorno
 * previsto para focar no fluxo de saída.
 *
 * @example
 * <PickupRentalsTable
 *   rentals={list.data}
 *   loading={list.loading}
 *   currentPage={list.page}
 *   totalPages={list.totalPages}
 *   onSelect={openPickupDialog}
 *   onPageChange={list.setPage}
 * />
 */
export const PickupRentalsTable = memo(function PickupRentalsTable({
  rentals,
  loading,
  currentPage,
  totalPages,
  onSelect,
  onPageChange,
}: PickupRentalsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Contrato</TableHead>
              <TableHead>Saída prevista</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Pago</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rentals.map((rental) => {
              const handleSelect = () => onSelect(rental);
              return (
                <TableRow
                  key={rental.id}
                  className="cursor-pointer hover:bg-accent/50"
                  onClick={handleSelect}
                >
                  <TableCell>{RENTAL_TYPE_LABELS[rental.rentalType]}</TableCell>
                  <TableCell>{rental.clientName}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {rental.contractNumber}
                  </TableCell>
                  <TableCell>{formatDateTime(rental.expectedOutAt)}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(rental.totalValue)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatCurrency(rental.paidValue)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect();
                      }}
                      title="Iniciar retirada"
                      aria-label="Iniciar retirada do aluguel"
                      disabled={loading}
                    >
                      <PackageOpen className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
